import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2, FileText, Folder } from 'lucide-react';
import { knowledgeService } from '@/services/knowledge.service';
import { ArticleCard } from '@/components/ArticleCard';
import { CategoryCard } from '@/components/CategoryCard';

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: category, isLoading: categoryLoading } = useQuery({
    queryKey: ['category', slug],
    queryFn: () => knowledgeService.getCategoryBySlug(slug!),
    enabled: !!slug,
  });

  const { data: breadcrumb } = useQuery({
    queryKey: ['category-breadcrumb', category?.id],
    queryFn: () => knowledgeService.getCategoryBreadcrumb(category!.id),
    enabled: !!category?.id,
  });

  const { data: articles, isLoading: articlesLoading } = useQuery({
    queryKey: ['category-articles', category?.id],
    queryFn: () =>
      knowledgeService.getPublishedArticles({
        categoryId: category!.id,
        limit: 50,
      }),
    enabled: !!category?.id,
  });

  if (categoryLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container py-16 text-center">
        <Folder className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Category Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The category you're looking for doesn't exist.</p>
        <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="container">
        {/* Breadcrumb */}
        {breadcrumb && (
          <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
            <Link to="/" className="hover:text-primary-600">
              Home
            </Link>
            {breadcrumb.map((item, index) => (
              <span key={item.id} className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                {index === breadcrumb.length - 1 ? (
                  <span className="text-gray-900 dark:text-white font-medium">{item.name}</span>
                ) : (
                  <Link to={`/category/${item.slug}`} className="hover:text-primary-600">
                    {item.name}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* Category Header */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
              <Folder className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{category.name}</h1>
              {category.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-2">{category.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Subcategories */}
        {category.children && category.children.length > 0 && (
          <section className="mb-12">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Subcategories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.children.map((child) => (
                <CategoryCard key={child.id} category={child} />
              ))}
            </div>
          </section>
        )}

        {/* Articles */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Articles in this category
          </h2>

          {articlesLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : articles?.data && articles.data.length > 0 ? (
            <div className="space-y-4">
              {articles.data.map((article) => (
                <ArticleCard key={article.id} article={article} showCategory={false} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p>No articles in this category yet.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
